import React, { useState, useEffect, useMemo } from "react";
import { Paper, Typography, Grid2, Divider, Box } from "@mui/material";
import { useForm, useFieldArray } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import { IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";
import ImagePicker from "@components/inputs/imagePicker";
import CustomIconButton from "@components/button/customIconButton";
import ControlledRichTextEditor from "@components/inputs/controlledRichTextEditor";
import RemarkDialog from "@components/dialog/remarkDialog";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints, AssetUrl } from "@configuration/apiEndpoints";
import { formatDateTime } from "@utils/formatter";

import { PAYMENT_TERMS } from "@utils/constant";

import { schema } from "./postFormSchema";

const isRegisterOptions = [
  {
    value: true,
    label: 'Yes',
  },
  {
    value: false,
    label: 'No',
  },
]

export default function PostForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch, setValue, formState: {errors} } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      postFeeOption: [{optionNameEng: "", optionNameCntrad: "", optionNameCnsimp: "", fee: "", sequence: ""}],
      paymentTerm: PAYMENT_TERMS[0],
      isRegister: isRegisterOptions[0],
    },
  });

  // console.log(errors)

  const { fields, append, remove } = useFieldArray({
    control,
    name: "postFeeOption",
  });

  const [thumbnail, setThumbnail] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [openVoidDialog, setOpenVoidDialog] = useState(false)


  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchPostMutation.mutate(id)
    }
  },[pageAction])

  const fetchPostMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getPost}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      // console.log(data)
      data.category = {categoryGuid: data?.category?.parameterGuid, categoryName: data?.category?.valueEng}
      data.postFeeOption = data?.postFeeOptions
      data.eventStatus = data?.eventStatus?.valueEng
      data.createdOn = formatDateTime(data?.createdOn)
      data.voidBy = data.voidBy ? data.voidBy : '-'
      data.voidDate = data.voidDate ? data.voidDate : '-'
      data.voidRemark = data.voidRemark ? data.voidRemark : '-'
      data.paymentTerm = PAYMENT_TERMS.find(term => term.value === data.paymentTerm);
      data.isRegister = isRegisterOptions.find(isRegister => isRegister.value === data.isRegister);
      reset(data)
      setThumbnail({file: null, preview: `${AssetUrl}${data?.thumbnailPath}`})
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve post", "error");
    }
  });

  const categoryOptions = useQuery({
    queryKey: ['getPostCategoryList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getPostCategoryList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        categoryGuid: item.parameterGuid,
        categoryName: item.valueEng
      }));
    },
  })

  const formOptions = useQuery({
    queryKey: ['getFormList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getFormList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data
    },
  })

  const userOptions = useQuery({
    queryKey: ['getUserList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getUserList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data
    },
  })

  const createPostMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createPost, data, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getPost'] })
      openSnackbar("Successfully created post", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create post", "error");
    }
  })

  const updatePostMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updatePost}/${id}`, data, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getPost'] })
      openSnackbar("Successfully updated post", "success");
      fetchPostMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update post", "error");
    }
  })

  const voidPostMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.voidPost}/${id}`, data)
    },
    onSuccess: () => {
      setOpenVoidDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getPostRecordsList'] })
      openSnackbar("Successfully voided post", "success");
      fetchPostMutation.mutate(id)
    },
    onError: (err) => {
      setOpenVoidDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to void post", "error");
    }
  })

  const onSubmit = () => {
    if(!thumbnail){
      openSnackbar(`Please upload thumbnail`, "error");
      return;
    }
    setOpenConfirmDialog(true)
  };

  const onConfirmVoid = (data) => {
    voidPostMutation.mutate({voidRemark: data.remark, by: user?.username})
  }

  const onConfirmSubmit = () => {
    const data = getValues();
    // console.log(data)
    // return

    const isRegister = data.isRegister?.value
    const formData = new FormData()

    formData.append("categoryGuid", data?.category?.categoryGuid);
    formData.append("formGuid", isRegister ? data?.form?.formGuid : "");  //

    formData.append("incharge1Guid", data?.incharge1?.userGuid);
    formData.append("incharge2Guid", data?.incharge2?.userGuid);
    formData.append("postName", data?.postName);
    formData.append("postDate", dayjs(data.postDate).format("YYYY-MM-DD"));
    formData.append("titleCntrad", data?.titleCntrad);
    formData.append("contentCntrad", data?.contentCntrad);
    formData.append("titleCnsimp", data?.titleCnsimp);
    formData.append("contentCnsimp", data?.contentCnsimp);
    formData.append("titleEng", data?.titleEng);
    formData.append("contentEng", data?.contentEng);

    formData.append("maxApplication", isRegister ? data?.maxApplication : ""); //
    formData.append("applyStartDate", isRegister ? dayjs(data.applyStartDate).format("YYYY-MM-DD") : ""); //
    formData.append("applyCloseDate", isRegister ? dayjs(data.applyCloseDate).format("YYYY-MM-DD") : ""); //
    formData.append("maxApplyQty", isRegister ? parseInt(data?.maxApplyQty) : ""); //
    formData.append("maxAcceptQty", isRegister ? parseInt(data?.maxAcceptQty) : ""); //
    formData.append("eventStartDate", dayjs(data.eventStartDate).format("YYYY-MM-DD"));
    formData.append("eventEndDate", dayjs(data.eventEndDate).format("YYYY-MM-DD"));
    formData.append("paymentTerm", isRegister ? data.paymentTerm.value : ""); //
    formData.append("isRegister", isRegister)
    formData.append("by", user?.username);
    formData.append("postFeeOptionJson", isRegister ? JSON.stringify(data?.postFeeOption) : null);  //
    formData.append("postThumbnail", thumbnail?.file ? thumbnail?.file : null);
    if(pageAction === "create"){
      createPostMutation.mutate(formData)
    }
    else{
      updatePostMutation.mutate(formData)
    }
  }

  const onBack = () => {
    navigate('/admin/post', { replace: true });
  }

  // const watchApplyStartDate = watch('applyStartDate')

  const watchEventStatus = watch('eventStatus')
  const watchIsRegister = watch('isRegister')?.value
  const watchPostFeeOption = watch('postFeeOption')

  useEffect(()=>{
    if(pageAction === "create"){
      if(watchIsRegister){
        setValue('postFeeOption', [{optionNameEng: "", optionNameCntrad: "", optionNameCnsimp: "", fee: "", sequence: ""}])
      }
      else{
        setValue('postFeeOption', [])
      }
    }
    if(pageAction === "edit"){
      if(watchIsRegister && watchPostFeeOption && watchPostFeeOption.length === 0){
        setValue('postFeeOption', [{optionNameEng: "", optionNameCntrad: "", optionNameCnsimp: "", fee: "", sequence: ""}])
      }
      if(!watchIsRegister){
        setValue('postFeeOption', [])
      }
    }
  },[watchIsRegister, pageAction])

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (["edit"].includes(pageAction) && watchEventStatus) &&
          <CustomButton color="#D13F58" disabled={watchEventStatus === "Voided"} onClick={()=>setOpenVoidDialog(true)}>Void</CustomButton>
        }
      />
      <SkeletonWrapper isLoading={fetchPostMutation.isPending} isError={fetchPostMutation.isError} errorMessage="Unable to load post" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Post Details</Typography>
            </Grid2>

            <Grid2 size={12}>
              <ImagePicker 
                name="thumbnail"
                label={"Upload Thumbnail (320x430)"}
                value={thumbnail}
                onChange={setThumbnail}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="postName"
                control={control}
                label="Post Name"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="category"
                control={control}
                label="Category"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                options={categoryOptions.data}
                loading={categoryOptions.isFetching}
                getOptionLabel={(option) => option.categoryName}
                getOptionKey={(option) => option.categoryGuid}
                isOptionEqualToValue={(option, value) => option.categoryGuid === value?.categoryGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="isRegister"
                control={control}
                options={isRegisterOptions}
                label="Is Registration Required?"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                disableClearable
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="postDate"
                control={control}
                label="Post Date"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            {watchIsRegister && 
            <>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="maxApplication"
                control={control}
                label="Max Application"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
                type="number"
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="form"
                control={control}
                loading={formOptions.isFetching}
                options={formOptions.data}
                label="Form Name"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                getOptionLabel={(option) => option.formName}
                getOptionKey={(option) => option.formGuid}
                isOptionEqualToValue={(option, value) => option.formGuid === value?.formGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="paymentTerm"
                control={control}
                options={PAYMENT_TERMS}
                label="Payment Term"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...optionProps}
                      style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start'}}
                    >
                      <div>
                        {option?.label}
                      </div>
                      <Typography fontSize={"11px"} color="#5D5D5D">{option?.description}</Typography>
                    </Box>
                  );
                }}
              />
            </Grid2>
            </>
            }

            

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>活动内容 (简体)</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="titleCnsimp"
                control={control}
                label="标题"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCnsimp"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>活動內容 (繁体)</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="titleCntrad"
                control={control}
                label="標題"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCntrad"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Activity Content</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="titleEng"
                control={control}
                label="Title"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentEng"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Application Details</Typography>
            </Grid2>

            {watchIsRegister && 
            <>
            
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="applyStartDate"
                control={control}
                label="Apply Start Date"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="applyCloseDate"
                control={control}
                label="Apply Close Date"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                // minDate={watchApplyStartDate ? new Date(watchApplyStartDate) : null}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="maxApplyQty"
                control={control}
                label="Max Apply Qty"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
                type="number"
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="maxAcceptQty"
                control={control}
                label="Max Accept Qty"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                readOnly={["view"].includes(pageAction)}
                type="number"
              />
            </Grid2>

            </>}

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="incharge1"
                control={control}
                loading={userOptions.isFetching}
                options={userOptions.data}
                label="In-charge 1"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                getOptionLabel={(option) => option.fullName}
                getOptionKey={(option) => option.userGuid}
                isOptionEqualToValue={(option, value) => option.userGuid === value?.userGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="incharge2"
                control={control}
                loading={userOptions.isFetching}
                options={userOptions.data}
                label="In-charge 2"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
                getOptionLabel={(option) => option.fullName}
                getOptionKey={(option) => option.userGuid}
                isOptionEqualToValue={(option, value) => option.userGuid === value?.userGuid}
              />
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Event Details</Typography>
            </Grid2>
            
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="eventStartDate"
                control={control}
                label="Event Start Date"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="eventEndDate"
                control={control}
                label="Event End Date"
                disabled={fetchPostMutation.isPending || fetchPostMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && 
              <Grid2 size={12}>
                <ControlledTextField 
                  name={`eventStatus`}
                  control={control}
                  label={`Event Status`}
                  disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                  readOnly={true}
                />
              </Grid2>
            }

          </Grid2>
        </Paper>

        {watchIsRegister && 
        <>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Fee Options</Typography>
            </Grid2>
            {fields.map((item, index) => (
            <React.Fragment key={item.id}>

              <Grid2 size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`postFeeOption.${index}.optionNameCntrad`}
                  control={control}
                  label={`選項 ${index + 1}`}
                  disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`postFeeOption.${index}.optionNameCnsimp`}
                  control={control}
                  label={`选项 ${index + 1}`}
                  disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`postFeeOption.${index}.optionNameEng`}
                  control={control}
                  label={`Option ${index + 1} Name`}
                  disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: "grow", md: 3, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`postFeeOption.${index}.fee`}
                  control={control}
                  label={`Option ${index + 1} Fee`}
                  disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                  type="number"
                />
              </Grid2>

              <Grid2 size={{ lg: "grow", md: 3, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name={`postFeeOption.${index}.sequence`}
                  control={control}
                  label={`Option ${index + 1} Sequence`}
                  disabled={fetchPostMutation.isPending || fetchPostMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                  type="number"
                />
              </Grid2>

              {(["create", "edit"].includes(pageAction) && fields?.length > 1) &&
                <Grid2 size={{ lg: "auto", md: "auto", sm: 12, xs: 12 }} display={"flex"} justifyContent={"flex-end"} alignItems={"flex-start"}>
                  <CustomIconButton aria-label="Remove" color="#D13F58" onClick={() => remove(index)}>
                    <IconTrash />
                  </CustomIconButton>
                </Grid2>
              }

              <Grid2 size={{ xs: 12 }}>
                <Divider />
              </Grid2>

              {(["create", "edit"].includes(pageAction) && index === fields?.length - 1 && index < 2) && 
                <Grid2 size={{ xs: 12 }} display={"flex"} justifyContent={"flex-end"} gap={"10px"}>
                  <CustomButton onClick={() => append({optionNameEng: "", optionNameCntrad: "", fee: ""})}>
                    Add Option
                  </CustomButton>
                </Grid2>
              }
            </React.Fragment>
          ))}

          </Grid2>
        </Paper>

        </>
        }

        {["view", "edit"].includes(pageAction) && 
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Other Details</Typography>
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="postNumber"
                control={control}
                label="Post No"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="voidBy"
                control={control}
                label="Void By"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="voidDate"
                control={control}
                label="Void Date"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="voidRemark"
                control={control}
                label="Void Remark"
                disabled={true}
              />
            </Grid2>
          </Grid2>
        </Paper>
        }

        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchPostMutation.isPending || fetchPostMutation.isError || watchEventStatus === "Voided"}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit post?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createPostMutation.isPending || updatePostMutation.isPending}
      />
      <RemarkDialog 
        open={openVoidDialog}
        onClose={()=>setOpenVoidDialog(false)}
        dialogTitle="Void Post"
        onSubmit={onConfirmVoid}
        loading={voidPostMutation.isPending}
      />
    </div>
  );
}
